import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { showSuccess, showError, showWarning } from "../utils/toastUtils";
import ConfirmModal from "../utils/ConfirmModal";
import "../stili/ScadenzaRate.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function ScadenzaRateForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [scadenze, setScadenze] = useState([]);
  const [stagioni, setStagioni] = useState([]);
  const [categorie, setCategorie] = useState([]);

  const [filtroIdStagione, setFiltroIdStagione] = useState("");
  const [filtroIdCategoria, setFiltroIdCategoria] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [filtroTipologia, setFiltroTipologia] = useState("");

  const [formData, setFormData] = useState({
    id: null,
    dataScadenza: "",
    importoPrevisto: "",
    stato: "ATTIVO",
    tipologia: "",
    idStagione: "",
    idCategoria: ""
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [scadenzaDaDisattivare, setScadenzaDaDisattivare] = useState(null);

  const statiPossibili = ["ATTIVO", "DISATTIVO"];
  const tipologiePossibili = ["PRE_ISCRIZIONE", "ISCRIZIONE", "RATA", "SALDO"];

  const tipologiaLabel = (value) => {
    switch (value) {
      case "PRE_ISCRIZIONE":
        return "Pre-iscrizione";
      case "ISCRIZIONE":
        return "Iscrizione";
      case "RATA":
        return "Rata";
      case "SALDO":
        return "Saldo";
      default:
        return value || "-";
    }
  };

  const stagioneLabelById = useMemo(() => {
    const m = new Map();
    stagioni.forEach((s) => m.set(String(s.id), s.nome));
    return m;
  }, [stagioni]);

  const categoriaLabelById = useMemo(() => {
    const m = new Map();
    categorie.forEach((c) => m.set(String(c.id), c.nome));
    return m;
  }, [categorie]);

  useEffect(() => {
    const fetchLookup = async () => {
      try {
        const [resStagioni, resCategorie] = await Promise.all([
          authFetch(`${API_URL}/api/admin/lookup/stagioni`),
          authFetch(`${API_URL}/api/admin/lookup/categorie`)
        ]);

        if (!resStagioni.ok) throw new Error("Errore caricamento stagioni");
        if (!resCategorie.ok) throw new Error("Errore caricamento categorie");

        const [dataStagioni, dataCategorie] = await Promise.all([
          resStagioni.json(),
          resCategorie.json()
        ]);

        setStagioni(Array.isArray(dataStagioni) ? dataStagioni : []);
        setCategorie(Array.isArray(dataCategorie) ? dataCategorie : []);
      } catch (error) {
        showError(error.message || "Errore nel caricamento dei dati iniziali.");
      }
    };

    fetchLookup();
  }, [authFetch]);

  const buildScadenzeUrl = useCallback(() => {
    const params = new URLSearchParams();

    if (filtroIdStagione) params.append("idStagione", filtroIdStagione);
    if (filtroIdCategoria) params.append("idCategoria", filtroIdCategoria);
    if (filtroStato) params.append("stato", filtroStato);
    if (filtroTipologia) params.append("tipologia", filtroTipologia);

    const qs = params.toString();
    return `${API_URL}/api/admin/scadenze${qs ? `?${qs}` : ""}`;
  }, [filtroIdStagione, filtroIdCategoria, filtroStato, filtroTipologia]);

  const fetchScadenze = useCallback(async () => {
    try {
      const response = await authFetch(buildScadenzeUrl());

      if (!response.ok) {
        let msg = "Errore nel caricamento delle scadenze";
        try {
          const errJson = await response.json();
          msg = errJson.message || errJson.error || JSON.stringify(errJson);
        } catch {
          const txt = await response.text().catch(() => "");
          if (txt) msg = txt;
        }
        throw new Error(msg);
      }

      const data = await response.json();
      setScadenze(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(error.message || "Errore nel caricamento delle scadenze.");
      setScadenze([]);
    }
  }, [authFetch, buildScadenzeUrl]);

  useEffect(() => {
    fetchScadenze();
  }, [fetchScadenze]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      id: null,
      dataScadenza: "",
      importoPrevisto: "",
      stato: "ATTIVO",
      tipologia: "",
      idStagione: "",
      idCategoria: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.idStagione) {
      showWarning("Seleziona la stagione.");
      return;
    }

    if (!formData.tipologia) {
      showWarning("Seleziona la tipologia.");
      return;
    }

    if (!formData.dataScadenza) {
      showWarning("Inserisci la data di scadenza.");
      return;
    }

    if (formData.importoPrevisto === "") {
      showWarning("Inserisci l'importo previsto.");
      return;
    }

    const metodo = formData.id ? "PUT" : "POST";
    const url = formData.id
      ? `${API_URL}/api/admin/scadenze/${formData.id}`
      : `${API_URL}/api/admin/scadenze`;

    const payload = {
      dataScadenza: formData.dataScadenza,
      importoPrevisto:
        formData.importoPrevisto === "" ? null : Number(formData.importoPrevisto),
      stato: formData.stato,
      tipologia: formData.tipologia,
      idStagione: Number(formData.idStagione),
      idCategoria: formData.idCategoria ? Number(formData.idCategoria) : null
    };

    try {
      const response = await authFetch(url, {
        method: metodo,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let msg = "Errore nel salvataggio";
        try {
          const errJson = await response.json();
          msg = errJson.message || errJson.error || JSON.stringify(errJson);
        } catch {
          const txt = await response.text().catch(() => "");
          if (txt) msg = txt;
        }
        throw new Error(msg);
      }

      showSuccess(formData.id ? "Scadenza aggiornata con successo." : "Scadenza aggiunta con successo.");
      resetForm();
      fetchScadenze();
    } catch (error) {
      showError(error.message || "Errore nel salvataggio.");
    }
  };

  const handleEdit = (scadenza) => {
    setFormData({
      id: scadenza.id,
      dataScadenza: scadenza.dataScadenza || "",
      importoPrevisto: scadenza.importoPrevisto ?? "",
      stato: scadenza.stato || "ATTIVO",
      tipologia: scadenza.tipologia || "",
      idStagione: scadenza.idStagione ? String(scadenza.idStagione) : "",
      idCategoria: scadenza.idCategoria ? String(scadenza.idCategoria) : ""
    });
  };

  const handleDeleteClick = (id) => {
    setScadenzaDaDisattivare(id);
    setDeleteModalOpen(true);
  };

  const confermaDisattivazione = async () => {
    if (!scadenzaDaDisattivare) return;

    try {
      const res = await authFetch(`${API_URL}/api/admin/scadenze/${scadenzaDaDisattivare}`, {
        method: "DELETE"
      });

      if (!res.ok) {
        let msg = "Errore nella disattivazione";
        try {
          const errJson = await res.json();
          msg = errJson.message || errJson.error || JSON.stringify(errJson);
        } catch {
          const txt = await res.text().catch(() => "");
          if (txt) msg = txt;
        }
        throw new Error(msg);
      }

      showSuccess("Scadenza disattivata con successo.");
      setDeleteModalOpen(false);
      setScadenzaDaDisattivare(null);
      fetchScadenze();
    } catch (error) {
      showError(error.message || "Errore nella disattivazione.");
      setDeleteModalOpen(false);
      setScadenzaDaDisattivare(null);
    }
  };

  const annullaDisattivazione = () => {
    setDeleteModalOpen(false);
    setScadenzaDaDisattivare(null);
    showWarning("Disattivazione annullata.");
  };

  const resetFiltri = () => {
    setFiltroIdStagione("");
    setFiltroIdCategoria("");
    setFiltroStato("");
    setFiltroTipologia("");
  };

  const tornaAlGestionale = () => navigate("/gestionale");
  const statoInEdit = useMemo(() => Boolean(formData.id), [formData.id]);

  const scadenzeOrdinate = useMemo(() => {
    return [...scadenze].sort((a, b) => {
      const stagioneA = stagioneLabelById.get(String(a.idStagione)) || "";
      const stagioneB = stagioneLabelById.get(String(b.idStagione)) || "";
      const cmpStagione = stagioneB.localeCompare(stagioneA);
      if (cmpStagione !== 0) return cmpStagione;

      const ordineA = Number(a.ordine ?? 9999);
      const ordineB = Number(b.ordine ?? 9999);
      if (ordineA !== ordineB) return ordineA - ordineB;

      const dataA = a.dataScadenza || "";
      const dataB = b.dataScadenza || "";
      return dataA.localeCompare(dataB);
    });
  }, [scadenze, stagioneLabelById]);

  return (
    <div className="scadenze-container" translate="no">
      <h2 className="scadenze-title">Gestione Scadenze Rate</h2>

      <div className="scadenze-card">
        <div className="scadenze-section">
          <h3 className="scadenze-section-title">Filtri</h3>

          <div className="scadenze-grid-4">
            <div className="scadenze-form-group">
              <label className="scadenze-label">Filtro Stagione</label>
              <select className="scadenze-select" value={filtroIdStagione} onChange={(e) => setFiltroIdStagione(e.target.value)}>
                <option value="">Tutte</option>
                {stagioni.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="scadenze-form-group">
              <label className="scadenze-label">Filtro Categoria</label>
              <select className="scadenze-select" value={filtroIdCategoria} onChange={(e) => setFiltroIdCategoria(e.target.value)}>
                <option value="">Tutte</option>
                {categorie.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="scadenze-form-group">
              <label className="scadenze-label">Filtro Stato</label>
              <select className="scadenze-select" value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)}>
                <option value="">Tutti</option>
                {statiPossibili.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="scadenze-form-group">
              <label className="scadenze-label">Filtro Tipologia</label>
              <select className="scadenze-select" value={filtroTipologia} onChange={(e) => setFiltroTipologia(e.target.value)}>
                <option value="">Tutte</option>
                {tipologiePossibili.map((t) => (
                  <option key={t} value={t}>
                    {tipologiaLabel(t)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="scadenze-actions">
            <button type="button" className="scadenze-btn-primary" onClick={fetchScadenze}>
              Applica filtri
            </button>

            <button type="button" className="scadenze-btn-secondary" onClick={resetFiltri}>
              Reset filtri
            </button>
          </div>
        </div>
      </div>

      <div className="scadenze-card">
        <div className="scadenze-section">
          <h3 className="scadenze-section-title">
            {statoInEdit ? "Modifica scadenza" : "Nuova scadenza"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div className="scadenze-grid-3">
              <div className="scadenze-form-group">
                <label className="scadenze-label">Stagione</label>
                <select className="scadenze-select" name="idStagione" value={formData.idStagione} onChange={handleChange} required>
                  <option value="">-- Seleziona Stagione --</option>
                  {stagioni.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="scadenze-form-group">
                <label className="scadenze-label">Categoria (opzionale)</label>
                <select className="scadenze-select" name="idCategoria" value={formData.idCategoria} onChange={handleChange}>
                  <option value="">-- Tutte le categorie --</option>
                  {categorie.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="scadenze-form-group">
                <label className="scadenze-label">Tipologia</label>
                <select className="scadenze-select" name="tipologia" value={formData.tipologia} onChange={handleChange} required>
                  <option value="">-- Seleziona Tipologia --</option>
                  {tipologiePossibili.map((t) => (
                    <option key={t} value={t}>
                      {tipologiaLabel(t)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="scadenze-form-group">
                <label className="scadenze-label">Stato</label>
                <select className="scadenze-select" name="stato" value={formData.stato} onChange={handleChange} required>
                  {statiPossibili.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="scadenze-form-group">
                <label className="scadenze-label">Data Scadenza</label>
                <input
                  className="scadenze-input"
                  type="date"
                  name="dataScadenza"
                  value={formData.dataScadenza}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="scadenze-form-group">
                <label className="scadenze-label">Importo Previsto</label>
                <input
                  className="scadenze-input"
                  type="number"
                  name="importoPrevisto"
                  value={formData.importoPrevisto}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                />
              </div>
            </div>

            <div className="scadenze-actions">
              <button type="submit" className="scadenze-btn-primary">
                {statoInEdit ? "Aggiorna" : "Aggiungi"}
              </button>

              {statoInEdit && (
                <button type="button" className="scadenze-btn-secondary" onClick={resetForm}>
                  Annulla modifica
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {scadenzeOrdinate.length === 0 ? (
        <div className="scadenze-empty-state">Nessuna scadenza presente.</div>
      ) : (
        <div className="scadenze-cards-grid">
          {scadenzeOrdinate.map((sc) => (
            <article className="scadenze-item-card" key={sc.id}>
              <div className="scadenze-item-header">
                <h3 className="scadenze-item-title">{tipologiaLabel(sc.tipologia)}</h3>
                <span className={`scadenze-status-badge ${String(sc.stato || "").toLowerCase()}`}>
                  {sc.stato || "-"}
                </span>
              </div>

              <div className="scadenze-item-body">
                <div className="scadenze-info-row">
                  <span className="scadenze-info-label">Ordine</span>
                  <span className="scadenze-info-value">{sc.ordine ?? "-"}</span>
                </div>

                <div className="scadenze-info-row">
                  <span className="scadenze-info-label">Data Scadenza</span>
                  <span className="scadenze-info-value">
                    {sc.dataScadenza
                      ? new Date(sc.dataScadenza).toLocaleDateString("it-IT")
                      : "-"}
                  </span>
                </div>

                <div className="scadenze-info-row">
                  <span className="scadenze-info-label">Importo</span>
                  <span className="scadenze-info-value">
                    {sc.importoPrevisto != null
                      ? `${Number(sc.importoPrevisto).toFixed(2)} €`
                      : "-"}
                  </span>
                </div>

                <div className="scadenze-info-row">
                  <span className="scadenze-info-label">Stagione</span>
                  <span className="scadenze-info-value">
                    {stagioneLabelById.get(String(sc.idStagione)) || "-"}
                  </span>
                </div>

                <div className="scadenze-info-row">
                  <span className="scadenze-info-label">Categoria</span>
                  <span className="scadenze-info-value">
                    {sc.idCategoria
                      ? categoriaLabelById.get(String(sc.idCategoria)) || "-"
                      : "Tutte"}
                  </span>
                </div>
              </div>

              <div className="scadenze-actions">
                <button type="button" className="scadenze-btn-primary" onClick={() => handleEdit(sc)}>
                  Modifica
                </button>

                <button type="button" className="scadenze-btn-danger" onClick={() => handleDeleteClick(sc.id)}>
                  Disattiva
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="scadenze-button-row">
        <button type="button" className="scadenze-btn-secondary" onClick={tornaAlGestionale}>
          Torna al Gestionale
        </button>
      </div>

      <ConfirmModal
        open={deleteModalOpen}
        title="Disattiva scadenza"
        message="Sei sicuro di voler disattivare questa scadenza? L'operazione non può essere annullata."
        confirmText="Disattiva"
        cancelText="Annulla"
        danger={true}
        onConfirm={confermaDisattivazione}
        onCancel={annullaDisattivazione}
      />
    </div>
  );
}